// src/converters/match_v2.ts

import { HyperlinkTarget, MatchV2, Stage, Team } from '@/model';
import {
  GetMatchesV2Response,
  GetMatchV2ByIdResponse,
  MatchV2ItemResponse,
  PostMatchV2Response,
  PostMatchesGenerateResponse,
  PutMatchV2Response,
  StageLiteResponse,
  TeamLiteResponse,
} from '@/services/response/match_v2';

const mapTeam = (response: TeamLiteResponse): Team => ({
  uuid: response.uuid,
  name: response.name,
  acronym: response.acronym,
  logo_path: response.logo_path,
} as unknown as Team);

const mapStage = (response: StageLiteResponse | null | undefined): Stage | undefined => {
  if (!response) return undefined;
  return ({
    uuid: response.uuid,
    name: response.name,
    group_name: response.group_name,
    type: response.type,
    is_main_widget: response.is_main_widget,
    stage_order: response.stage_order,
    current_round: response.current_round,
    season: response.season,
  } as unknown) as Stage;
};

const mapMatchItem = (response: MatchV2ItemResponse): MatchV2 => ({
  uuid: response.uuid,
  home_team_uuid: response.home_team.uuid,
  home_team: mapTeam(response.home_team),
  away_team_uuid: response.away_team.uuid,
  away_team: mapTeam(response.away_team),
  match_date: response.match_date ?? undefined,
  transmission: response.transmission ?? undefined,
  stadium: response.stadium ?? undefined,
  hyperlink: response.hyperlink ?? undefined,
  hyperlink_target: response.hyperlink_target ?? HyperlinkTarget.EXTERNAL,
  description: response.description ?? undefined,
  round: response.round ?? 0,
  home_goals: response.home_goals ?? undefined,
  away_goals: response.away_goals ?? undefined,
  extra_time_home: response.extra_time_home ?? undefined,
  extra_time_away: response.extra_time_away ?? undefined,
  penalties_home: response.penalties_home ?? undefined,
  penalties_away: response.penalties_away ?? undefined,
  status: response.status,
  is_show_information: response.is_show_information,
  is_last_matches: response.is_last_matches ?? false,
  last_matches: response.last_matches ?? [],
  is_history_matches: response.is_history_matches ?? false,
  history_matches: response.history_matches ?? undefined,
  created_at: response.created_at,
  updated_at: response.updated_at,
  stage: mapStage(response.stage),
});

export class MatchV2Converter {
  static toDomainFromPaginatedResponse(response: GetMatchesV2Response): MatchV2[] {
    return response.data.map(mapMatchItem);
  }

  static toDomainFromByIdResponse(response: GetMatchV2ByIdResponse): MatchV2 {
    return mapMatchItem(response);
  }

  static toDomainFromPostResponse(response: PostMatchV2Response): MatchV2 {
    return mapMatchItem(response);
  }

  static toDomainFromPutResponse(response: PutMatchV2Response): MatchV2 {
    return mapMatchItem(response);
  }

  static toDomainFromGenerateResponse(response: PostMatchesGenerateResponse): {
    data: MatchV2[];
    meta: PostMatchesGenerateResponse['meta'];
  } {
    return {
      data: response.data.map(mapMatchItem),
      meta: response.meta,
    };
  }
}
